<script setup>
import { reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  assistant: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['submit']);

const { t } = useI18n();

const state = reactive({ glossary: '' });
const fileInput = ref(null);

const setFromAssistant = () => {
  state.glossary = props.assistant?.config?.glossary || '';
};

watch(() => props.assistant, setFromAssistant, { immediate: true });

const triggerUpload = () => fileInput.value?.click();

const onFileSelected = event => {
  const file = event.target.files?.[0];
  if (!file) return;
  const reader = new FileReader();
  reader.onload = e => {
    // append uploaded markdown to whatever is already there
    const text = e.target.result || '';
    state.glossary = state.glossary ? `${state.glossary}\n${text}` : text;
  };
  reader.readAsText(file);
  event.target.value = '';
};

const handleSubmit = () => {
  emit('submit', {
    config: {
      ...props.assistant.config,
      glossary: state.glossary,
    },
  });
};
</script>

<template>
  <div class="flex flex-col gap-3">
    <p class="text-sm text-n-slate-11">
      Add a terminology / translation reference (Markdown). The assistant uses it
      to understand how customers phrase things — for example Arabic dialect terms
      and what they mean in the system. Keep it focused (terms and their meanings),
      not a large document.
    </p>

    <textarea
      v-model="state.glossary"
      rows="12"
      dir="auto"
      placeholder="e.g.
- تجزئة / بدي اجزء منتج / تقسيم منتج → create a variant (product variant)
- أمر شراء → purchase order (PO)
- نقطة بيع → point of sale (POS)
- مرتجع → refund / return"
      class="w-full px-3 py-2 text-sm rounded-lg border border-solid border-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-woot-500 font-mono"
    />

    <input
      ref="fileInput"
      type="file"
      accept=".md,.markdown,.txt"
      class="hidden"
      @change="onFileSelected"
    />

    <div class="flex items-center gap-2">
      <Button
        variant="faded"
        color="slate"
        type="button"
        label="Upload .md file"
        icon="i-lucide-upload"
        @click="triggerUpload"
      />
      <Button
        type="button"
        :label="t('CAPTAIN.ASSISTANTS.EDIT.SUBMIT', 'Save')"
        @click="handleSubmit"
      />
    </div>
  </div>
</template>
